import { Suspense, useEffect } from "react";
import { lazyWithRetry as lazy, clearChunkReloadFlag } from "@/lib/lazyWithRetry";
import { savePendingDeepLink } from "@/lib/notifications/pendingDeepLink";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate, useLocation, useParams } from "react-router-dom";
import { ThemeProvider } from "@/components/ThemeProvider";
import { FINANCE_HOST, HR_HOST } from "@/lib/hosts";
import Navigation from "./components/Navigation";
import BottomTabs from "./components/BottomTabs";
import Footer from "./components/Footer";
import ScrollToTop from "./components/ScrollToTop";
// PWAInstallPrompt removed — hibane.ai (marketing) is no longer a PWA.
// The HIBANE Desk install prompt lives on /admin/login + inside /admin/* via
// src/components/admin/AdminInstallReminder.tsx. See
// .hibane/contracts/marketing-no-pwa.md.
import { AnalyticsTracker } from "./components/AnalyticsTracker";
import { AIConcierge } from "./components/AIConcierge";
import PageLoader from "./components/PageLoader";
import { RouteFallback } from "./components/skeletons/RouteFallback";
import type { RouteSkeletonVariant } from "./components/skeletons/RouteSkeleton";
// SWUpdatePrompt intentionally NOT mounted on hibane.ai (marketing + Admin Desk).
// The "New version available" toast is owned by the HR Desk (hr.hibane.ai) and
// Finance Desk (finance.hibane.ai) Lovable projects only — see
// .hibane/contracts/sw-update-prompt.md.
import { UploadProvider } from "./components/uploads/UploadProvider";
import { ScopedUploadTray } from "./components/uploads/ScopedUploadTray";
import { MFAEnrollmentBanner } from "./components/security/MFAEnrollmentBanner";
import { TrialStatusBanner } from "./components/billing/TrialStatusBanner";
import VoiceProtectedRoute from "./components/VoiceProtectedRoute";
import { NativeAppBoot } from "./components/native/NativeAppBoot";
import Experience3D, { isCineRoute } from "./components/fx3d/Experience3D";
import CineOutro from "./components/cine/CineOutro";

// Lazy load all pages for code splitting
const SplashScreen = lazy(() => import("./pages/SplashScreen"));
const Home = lazy(() => import("./pages/Home"));

// Opening hibane.ai plays the splash reel once per browser session; its Get
// Started button marks it seen and returns here to the homepage. Crawlers
// skip it so "/" stays indexed as the homepage.
const SPLASH_SEEN_KEY = "hibane_splash_seen";
const RootRoute = () => {
  let seen = true;
  try {
    seen = sessionStorage.getItem(SPLASH_SEEN_KEY) === "1";
  } catch {
    /* storage blocked: go straight to the homepage */
  }
  const isBot = /bot|crawl|spider|slurp|lighthouse|headless/i.test(navigator.userAgent);
  return seen || isBot ? <Home /> : <Navigate to="/splash" replace />;
};
const About = lazy(() => import("./pages/About"));
const Services = lazy(() => import("./pages/Services"));
const Contact = lazy(() => import("./pages/Contact"));
const HubRegistration = lazy(() => import("./pages/HubRegistration"));
const AcademyRegistration = lazy(() => import("./pages/AcademyRegistration"));
const Curriculum = lazy(() => import("./pages/Curriculum"));
const News = lazy(() => import("./pages/News"));
const BlogPost = lazy(() => import("./pages/BlogPost"));
const NotFound = lazy(() => import("./pages/NotFound"));
const AdminLogin = lazy(() => import("./pages/AdminLogin"));
import { SocialsAuthFragmentBridge } from "@/components/socials/SocialsAuthFragmentBridge";
const AdminDashboard = lazy(() => import("./pages/AdminDashboard"));
const TrainWithHibane = lazy(() => import("./pages/TrainWithHibane"));
const PrivacyPolicy = lazy(() => import("./pages/PrivacyPolicy"));
const TermsOfService = lazy(() => import("./pages/TermsOfService"));
const BookConsultation = lazy(() => import("./pages/BookConsultation"));
const ConsultationIntro = lazy(() => import("./pages/ConsultationIntro"));
const BespokeConsultationIntro = lazy(() => import("./pages/BespokeConsultationIntro"));
const BespokeBookConsultation = lazy(() => import("./pages/BespokeBookConsultation"));
const OAuthCallback = lazy(() => import("./pages/OAuthCallback"));
const ApartmentBooking = lazy(() => import("./pages/ApartmentBooking"));
const MediaConsultation = lazy(() => import("./pages/MediaConsultation"));
const SocialPosts = lazy(() => import("./pages/SocialPosts"));
const Unsubscribe = lazy(() => import("./pages/Unsubscribe"));
const HibaneMedia = lazy(() => import("./pages/HibaneMedia"));
const TheEdgeWaitlist = lazy(() => import("./pages/TheEdgeWaitlist"));
const StudioPackage = lazy(() => import("./pages/StudioPackage"));
const StudioReschedule = lazy(() => import("./pages/StudioReschedule"));
const StudioReceipt = lazy(() => import("./pages/StudioReceipt"));
const PayMedia = lazy(() => import("./pages/PayMedia"));
const PayInvoice = lazy(() => import("./pages/PayInvoice"));

const KioskWelcome = lazy(() => import("./pages/KioskWelcome"));
const MediaPortfolio = lazy(() => import("./pages/MediaPortfolio"));
const MediaBooking = lazy(() => import("./pages/MediaBooking"));

const MediaDelivery = lazy(() => import("./pages/MediaDelivery"));
const MediaReview = lazy(() => import("./pages/MediaReview"));
const SharedFolder = lazy(() => import("./pages/SharedFolder"));
const SharedFile = lazy(() => import("./pages/SharedFile"));
// HR Desk lives in the standalone hibane-hr Lovable project (https://hr.hibane.ai).
// /hr and /hr/* are redirected below; no in-app pages remain here.
const PublicFormPage = lazy(() => import("./pages/PublicFormPage"));
const SaaSFinance = lazy(() => import("./pages/saas/SaaSFinance"));
const SaaSHR = lazy(() => import("./pages/saas/SaaSHR"));
const SaaSSuite = lazy(() => import("./pages/saas/SaaSSuite"));
const SaaSSignup = lazy(() => import("./pages/saas/SaaSSignup"));
const AccountSecurity = lazy(() => import("./pages/account/AccountSecurity"));
const AccountBilling = lazy(() => import("./pages/account/AccountBilling"));
const Upgrade = lazy(() => import("./pages/account/Upgrade"));
const AddProduct = lazy(() => import("./pages/account/AddProduct"));
const AuthHandoffFallback = lazy(() => import("./pages/AuthHandoffFallback"));
const VoiceAgentLanding = lazy(() => import("./pages/VoiceAgentLanding"));
const VoicePortal = lazy(() => import("./pages/voice/VoicePortal"));
const SocialsPortal = lazy(() => import("./pages/socials/SocialsPortal"));
const SocialsAcceptInvite = lazy(() => import("./pages/socials/AcceptInvite"));
const ScopeDownload = lazy(() => import("./pages/download/ScopeDownload"));
const InvoiceDownload = lazy(() => import("./pages/download/InvoiceDownload"));
import SocialsProtectedRoute from "./components/SocialsProtectedRoute";

// Phase 3 — shareable /voice/call/<id> deep link. 302 to the query-string form
// the VoicePortal actually parses. Destination is already gated by
// VoiceProtectedRoute, so this wrapper doesn't need its own auth check.
const VoiceCallDeepLinkRedirect = () => {
  const { id } = useParams<{ id: string }>();
  return <Navigate to={`/voice?tab=calls&sessionId=${encodeURIComponent(id || "")}`} replace />;
};

const queryClient = new QueryClient();


const AppContent = () => {
  const location = useLocation();
  const isSplashRoute = location.pathname === '/splash';
  const isAdminRoute = location.pathname.startsWith('/admin');
  const isHRRoute = location.pathname === '/hr' || location.pathname.startsWith('/hr/');
  const isVoicePortalRoute = location.pathname === '/voice' || location.pathname.startsWith('/voice/');
  const isSocialsPortalRoute = location.pathname === '/socials' || location.pathname.startsWith('/socials/');
  const isShareRoute = location.pathname.startsWith('/shared/');
  const isPublicFormRoute = location.pathname.startsWith('/f/');
  const isAuthHandoffRoute = location.pathname === '/auth/handoff';
  const isTheEdgeRoute = location.pathname === '/theedge' || location.pathname.startsWith('/theedge/');
  const isKioskRoute = location.pathname.startsWith('/welcome/');
  const isStudioRoute = location.pathname === '/studiopackage' || location.pathname.startsWith('/studiopackage/');
  // Public branded pay pages (/pay/media/:id, /pay/invoice/:id) — standalone chrome.
  const isPayRoute = location.pathname.startsWith('/pay/');
  // Voice agent landing now uses global chrome (TopBanner/Navigation/Footer/BottomTabs).

  // Legacy /finance/* and /hr/* routes now redirect to their standalone Lovable
  // projects (https://finance.hibane.ai and https://hr.hibane.ai). The Finance
  // and HR UIs no longer live in this project.
  useEffect(() => {
    if (location.pathname === '/finance' || location.pathname.startsWith('/finance/')) {
      const targetPath = location.pathname.replace(/^\/finance(?=\/|$)/, '') || '/dashboard';
      window.location.replace(`https://${FINANCE_HOST}${targetPath}${location.search}`);
      return;
    }
    if (location.pathname === '/hr' || location.pathname.startsWith('/hr/')) {
      const targetPath = location.pathname.replace(/^\/hr(?=\/|$)/, '') || '/dashboard';
      window.location.replace(`https://${HR_HOST}${targetPath}${location.search}`);
    }
  }, [location.pathname, location.search]);

  // Public marketing + form routes use the cinematic theme layer (index.css,
  // html[data-cine]); admin, portals and client-branded pages keep their own.
  useEffect(() => {
    document.documentElement.toggleAttribute("data-cine", isCineRoute(location.pathname));
  }, [location.pathname]);

  // Once the app successfully renders, allow future chunk-reload recovery again.
  useEffect(() => {
    clearChunkReloadFlag();
  }, []);

  // Capture deep-link URLs from the very first navigation so that protected-route
  // redirects to portal login pages don't lose them.
  useEffect(() => {
    try {
      const path = window.location.pathname;
      const search = window.location.search;
      const full = path + search;
      if (path.startsWith('/admin') && path !== '/admin/login') {
        savePendingDeepLink('admin', full);
      } else if (path.startsWith('/hr/') && path !== '/hr/login') {
        savePendingDeepLink('hr', full);
      } else if (path.startsWith('/voice') && path !== '/voice/login') {
        savePendingDeepLink('voice', full);
      } else if (path.startsWith('/socials') && path !== '/socials/login') {
        savePendingDeepLink('socials', full);
      }
    } catch { /* noop */ }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const hideMainLayout =
    isSplashRoute || isAdminRoute || isHRRoute || isVoicePortalRoute || isSocialsPortalRoute || isShareRoute || isPublicFormRoute || isAuthHandoffRoute || isTheEdgeRoute || isKioskRoute || isStudioRoute || isPayRoute;

  // Pick a layout-shaped skeleton fallback that matches the current surface so
  // users never see a blank screen during lazy-load (or the lazyWithRetry
  // retry window).
  const fallbackVariant: RouteSkeletonVariant = (() => {
    if (isSplashRoute) return "splash";
    if (isAdminRoute) return "admin";
    const p = location.pathname;
    if (
      p.startsWith("/book-consultation") ||
      p.startsWith("/bespoke-consultation/") && p.endsWith("/form") ||
      p.startsWith("/apartment-booking") ||
      p.startsWith("/media-booking") ||
      p === "/signup" ||
      p.startsWith("/account/") ||
      p === "/auth/handoff" ||
      p.startsWith("/f/") ||
      p.startsWith("/oauth/") ||
      p.startsWith("/shared/") ||
      p.startsWith("/media-delivery/") ||
      p.startsWith("/media-review/") ||
      p === "/unsubscribe"
    ) {
      return "form";
    }
    return "marketing";
  })();

  return (
    <>
      <NativeAppBoot />
      <AnalyticsTracker />
      <ScrollToTop />
      <Experience3D />
      {/* Public chrome: fixed side rail (desktop) + floating dock (mobile).
          Content is offset beside the rail instead of scrolling under a top bar. */}
      {!hideMainLayout && <Navigation />}
      <div className={`min-h-screen bg-background text-foreground flex flex-col ${!hideMainLayout ? "md:pl-[76px]" : ""}`}>
        {!hideMainLayout && <TrialStatusBanner />}
        {!hideMainLayout && <MFAEnrollmentBanner />}
        <main className={!hideMainLayout ? "pb-24 md:pb-0 flex-1" : "flex-1"}>
          <Suspense fallback={<RouteFallback variant={fallbackVariant} />}>
            <Routes>
              <Route path="/" element={<RootRoute />} />
              <Route path="/home" element={<Home />} />
              <Route path="/splash" element={<SplashScreen />} />
              <Route path="/about" element={<About />} />
              <Route path="/services" element={<Services />} />
              <Route path="/curriculum" element={<Curriculum />} />
              <Route path="/news" element={<News />} />
              <Route path="/news/:slug" element={<BlogPost />} />
              <Route path="/hub" element={<Navigate to="/hub-registration" replace />} />
              <Route path="/hub-registration" element={<HubRegistration />} />
              <Route path="/academy-registration" element={<AcademyRegistration />} />
              <Route path="/training-registration" element={<Navigate to="/trainwithhibane" replace />} />
              <Route path="/trainwithhibane" element={<TrainWithHibane />} />
              <Route path="/contact" element={<Contact />} />
              <Route path="/consultation-intro" element={<ConsultationIntro />} />
              <Route path="/book-consultation" element={<BookConsultation />} />
              <Route path="/privacy" element={<PrivacyPolicy />} />
              <Route path="/terms" element={<TermsOfService />} />
              <Route path="/consultation/:slug" element={<BespokeConsultationIntro />} />
              <Route path="/bespoke-consultation/:token" element={<BespokeConsultationIntro />} />
              <Route path="/bespoke-consultation/:token/form" element={<BespokeBookConsultation />} />
              <Route path="/oauth/callback" element={<OAuthCallback />} />
              <Route path="/apartment-booking" element={<ApartmentBooking />} />
              <Route path="/media-consultation/:slug" element={<MediaConsultation />} />
              <Route path="/social-posts" element={<SocialPosts />} />
              <Route path="/unsubscribe" element={<Unsubscribe />} />
              <Route path="/media" element={<HibaneMedia />} />
              <Route path="/theedge" element={<TheEdgeWaitlist />} />
              {/* Hibane Media studio retainer landing page — standalone chrome. */}
              <Route path="/studiopackage" element={<StudioPackage />} />
              <Route path="/studiopackage/reschedule" element={<StudioReschedule />} />
              <Route path="/studiopackage/receipt" element={<StudioReceipt />} />

              {/* Public branded pay-by-link pages — standalone chrome. */}
              <Route path="/pay/media/:id" element={<PayMedia />} />
              <Route path="/pay/invoice/:id" element={<PayInvoice />} />

              {/* Public multi-client kiosk (Phase 3) — branded per client, no HIBANE chrome. */}
              <Route path="/welcome/:slug" element={<KioskWelcome />} />
              <Route path="/media/portfolio" element={<MediaPortfolio />} />
              <Route path="/media-booking" element={<MediaBooking />} />

              <Route path="/media-delivery/:token" element={<MediaDelivery />} />
              <Route path="/media-review/:token" element={<MediaReview />} />
              <Route path="/shared/folder/:token" element={<SharedFolder />} />
              <Route path="/shared/file/:shareId/*" element={<SharedFile />} />
              {/* /hr and /hr/* redirect to the standalone HR Desk via the useEffect above. */}
              <Route path="/hr" element={<PageLoader />} />
              <Route path="/hr/*" element={<PageLoader />} />
              <Route path="/admin/login" element={<AdminLogin />} />
              <Route path="/admin/*" element={<AdminDashboard />} />
              <Route path="/f/:slug" element={<PublicFormPage />} />
              <Route path="/f/:slug/preview" element={<PublicFormPage previewMode />} />
              {/* /finance and /finance/* redirect to the standalone Finance Desk
                  via the useEffect above; render a loader while the redirect runs. */}
              <Route path="/finance" element={<PageLoader />} />
              <Route path="/finance/*" element={<PageLoader />} />
              {/* SaaS marketing & signup (Phase 1) */}
              <Route path="/saas" element={<Navigate to="/suite" replace />} />
              <Route path="/saas/finance" element={<Navigate to="/financedesk" replace />} />
              <Route path="/saas/hr" element={<Navigate to="/hrdesk" replace />} />
              <Route path="/saas/suite" element={<Navigate to="/suite" replace />} />
              <Route path="/saas/signup" element={<Navigate to="/signup" replace />} />
              <Route path="/financedesk" element={<SaaSFinance />} />
              <Route path="/hrdesk" element={<SaaSHR />} />
              <Route path="/suite" element={<SaaSSuite />} />
              <Route path="/signup" element={<SaaSSignup />} />
              <Route path="/account/security" element={<AccountSecurity />} />
              <Route path="/account/billing" element={<AccountBilling />} />
              <Route path="/upgrade" element={<Upgrade />} />
              <Route path="/account/add-product" element={<AddProduct />} />
              {/* Friendly fallback when /auth/handoff is opened on the marketing
                  domain instead of a product subdomain. Preserves the one-time
                  token + product param and forwards to the real handoff. */}
              <Route path="/auth/handoff" element={<AuthHandoffFallback />} />
              <Route path="/voiceagent" element={<VoiceAgentLanding />} />
              <Route path="/voice-agent-demo" element={<Navigate to="/voiceagent" replace />} />
              {/* Standalone HIBANE Voice Agent portal — shares admin auth +
                  voice_agent.* permission gate. Login screen reuses AdminLogin
                  (which detects /voice scope and redirects to /voice). */}
              <Route path="/voice/login" element={<AdminLogin />} />
              <Route
                path="/voice"
                element={
                  <VoiceProtectedRoute>
                    <VoicePortal />
                  </VoiceProtectedRoute>
                }
              />
              {/* Shareable deep-link form: /voice/call/<id> → /voice?tab=calls&sessionId=<id> */}
              <Route path="/voice/call/:id" element={<VoiceCallDeepLinkRedirect />} />
              <Route path="/voice/*" element={<Navigate to="/voice" replace />} />

              {/* Standalone HIBANE Social Studio portal — shares admin auth +
                  social-media permission gate. Login screen reuses AdminLogin
                  (which detects /socials scope and redirects to /socials). */}
              <Route path="/socials/login" element={<SocialsAuthFragmentBridge><AdminLogin /></SocialsAuthFragmentBridge>} />
              <Route path="/socials/accept-invite" element={<SocialsAcceptInvite />} />
              <Route
                path="/socials"
                element={
                  <SocialsProtectedRoute>
                    <SocialsPortal />
                  </SocialsProtectedRoute>
                }
              />
              <Route
                path="/socials/*"
                element={
                  <SocialsProtectedRoute>
                    <SocialsPortal />
                  </SocialsProtectedRoute>
                }
              />
              {/* Branded customer-facing download routes (token-gated) */}
              <Route path="/d/scope/:filename" element={<ScopeDownload />} />
              <Route path="/d/invoice/:filename" element={<InvoiceDownload />} />
              {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
              <Route path="*" element={<NotFound />} />
            </Routes>
          </Suspense>
          {/* closing robot act for public content pages (per-route table) */}
          {!hideMainLayout && <CineOutro />}
        </main>
        {!hideMainLayout && <Footer />}
        {!hideMainLayout && <BottomTabs />}
        {!isAdminRoute && !isHRRoute && !isVoicePortalRoute && !isShareRoute && !isPublicFormRoute && !isAuthHandoffRoute && !isTheEdgeRoute && !isKioskRoute && !isStudioRoute && !isPayRoute && <AIConcierge />}
      </div>
    </>
  );
};

const App = () => {
  return (
    <QueryClientProvider client={queryClient}>
        <ThemeProvider defaultTheme="light">
        <TooltipProvider delayDuration={200}>
          <Toaster />
          <Sonner />
          <BrowserRouter>
            <UploadProvider>
              <AppContent />
              <ScopedUploadTray />
            </UploadProvider>
          </BrowserRouter>
        </TooltipProvider>
      </ThemeProvider>
    </QueryClientProvider>
  );
};

export default App;
